import { useState, useMemo } from 'react';
import {
  ChakraBox,
  ChakraCheckbox,
  ChakraFlex,
  ChakraInput,
  ChakraInputGroup,
  ChakraInputLeftElement,
  ChakraInputRightElement,
  DatePickerInput,
  DropdownV2,
  FAIconName,
  FireflyButton,
  FireflyIconButton,
  Icon,
  IconSize,
  InputGroupV2,
  ModalV2,
  ProviderAvatarBlock,
  Tag,
  TagColor,
  Text,
  Tooltip,
  useToastMessage,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { PatientEvents } from '~/analytics/patientEvents';
import EquipLogo from '~/assets/svg/equip-logo.svg';
import SkeletonLoaderList from '~/components/common/SkeletonLoaderList';
import ProviderUniversalSelect from '~/components/common/calendar/ProviderUniversalSelect';
import {
  DATE_PREFERENCE_OPTIONS,
  INCLUDED_PROVIDER_TYPES,
  INITIATOR_TABS,
} from '~/components/maud/reassignment/constants';
import {
  getProviderTypeLabel,
  isPatientEligible,
} from '~/components/maud/reassignment/util';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useDatadog } from '~/lib/hooks/useDatadog';
import {
  getExactAgeFromDOB,
  getPreferredFullName,
} from '~/lib/util/patient.util';
import type {
  DatePreference,
  ReassignmentReasonCategory,
} from '~/lib/types/reassignment';
import {
  useCreateReassignmentsMutation,
  useGetCandidatePatientsQuery,
  useGetReassignmentReasonsQuery,
} from '~/store/services/clinicalData';

const TOP_LEVEL_ID = 'add-reassignment-modal';
const getTomorrow = () => DateTime.now().plus({ days: 1 });

const AddReassignmentModal = () => {
  const { addReassignmentModal, setComponentVisibility } =
    useComponentPortalContext();
  const { trackEvent } = useAnalytics();
  const { logError } = useDatadog();
  const { createToast } = useToastMessage();
  const [step, setStep] = useState<1 | 2>(1);
  const [initiatorTab, setInitiatorTab] =
    useState<ReassignmentReasonCategory>('EQUIP');
  const [selectedProvider, setSelectedProvider] =
    useState<ProviderUniversalSelectOption | null>(null);
  const [lastDayOfCare, setLastDayOfCare] = useState<string | null>(null);
  const [datePreference, setDatePreference] = useState<DatePreference | null>(
    null,
  );
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [selectedPatientIds, setSelectedPatientIds] = useState<Set<string>>(
    new Set(),
  );
  const [searchQuery, setSearchQuery] = useState('');

  const [createReassignments, { isLoading: isSubmitting }] =
    useCreateReassignmentsMutation();
  const { data: reasons = [] } = useGetReassignmentReasonsQuery();
  const { data: candidatePatients = [], isFetching: isFetchingPatients } =
    useGetCandidatePatientsQuery(selectedProvider?.userId ?? '', {
      skip: !selectedProvider?.userId,
    });

  const filteredReasons = useMemo(
    () =>
      reasons
        .filter((r) => r.category === initiatorTab)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((r) => ({ label: r.description, value: r.code })),
    [reasons, initiatorTab],
  );

  const sortedPatients = useMemo(
    () =>
      candidatePatients
        .filter((p) => p.firstName || p.lastName)
        .sort((a, b) => {
          const nameA = getPreferredFullName(a.chosenName, a.firstName, a.lastName).toLowerCase();
          const nameB = getPreferredFullName(b.chosenName, b.firstName, b.lastName).toLowerCase();
          return nameA.localeCompare(nameB);
        }),
    [candidatePatients],
  );

  const filteredPatients = useMemo(() => {
    if (!searchQuery.trim()) return sortedPatients;
    const q = searchQuery.toLowerCase();
    return sortedPatients.filter((p) => {
      const displayName = getPreferredFullName(p.chosenName, p.firstName, p.lastName).toLowerCase();
      const legalName = `${p.firstName ?? ''} ${p.lastName ?? ''}`.toLowerCase();
      return displayName.includes(q) || legalName.includes(q);
    });
  }, [sortedPatients, searchQuery]);

  const eligiblePatients = useMemo(
    () => sortedPatients.filter(isPatientEligible),
    [sortedPatients],
  );

  const isSearching = searchQuery.trim().length > 0;

  const allEligibleSelected =
    eligiblePatients.length > 0 &&
    eligiblePatients.every((p) => selectedPatientIds.has(p.patientExternalId));

  const isEquipInitiated = initiatorTab === 'EQUIP';

  const datePreferenceOptions = isEquipInitiated
    ? DATE_PREFERENCE_OPTIONS
    : DATE_PREFERENCE_OPTIONS.filter((o) => o.value !== 'LAST_DAY_OF_CARE');

  const isStep1Valid =
    selectedProvider !== null &&
    (!isEquipInitiated || lastDayOfCare !== null) &&
    datePreference !== null &&
    reasonCode !== null;

  const handleClose = () => {
    setComponentVisibility({
      component: PortalComponent.ADD_REASSIGNMENT_MODAL,
      data: null,
      isVisible: false,
    });
  };

  const handleCancel = () => {
    trackEvent(PatientEvents.reassignmentAbandoned, {
      source: AnalyticsSourceValues.PATIENT_LIST,
    });
    handleClose();
  };

  const handleProviderSelect = (
    option: ProviderUniversalSelectOption | ProviderUniversalSelectOption[],
  ) => {
    const provider = Array.isArray(option) ? option[0] : option;
    if (provider?.userId !== selectedProvider?.userId) {
      setSelectedPatientIds(new Set());
      setSearchQuery('');
    }
    setSelectedProvider(provider ?? null);
  };

  const handleInitiatorTabChange = (tab: ReassignmentReasonCategory) => {
    setInitiatorTab(tab);
    setReasonCode(null);
    if (tab === 'FAMILY') {
      setLastDayOfCare(null);
      if (datePreference === 'LAST_DAY_OF_CARE') {
        setDatePreference(null);
      }
    }
  };

  const handleTogglePatient = (patientExternalId: string) => {
    setSelectedPatientIds((prev) => {
      const next = new Set(prev);
      if (next.has(patientExternalId)) {
        next.delete(patientExternalId);
      } else {
        next.add(patientExternalId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (allEligibleSelected) {
      const eligibleIds = new Set(
        eligiblePatients.map((p) => p.patientExternalId),
      );
      setSelectedPatientIds((prev) => {
        const next = new Set(prev);
        eligibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedPatientIds((prev) => {
        const next = new Set(prev);
        eligiblePatients.forEach((p) => next.add(p.patientExternalId));
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    if (!isStep1Valid) return;

    try {
      await createReassignments({
        departingProviderId: selectedProvider.userId,
        providerTypeLabel: getProviderTypeLabel(selectedProvider),
        reassignmentReasonCode: reasonCode,
        datePreference,
        lastDayOfCare: lastDayOfCare ?? getTomorrow().toISODate(),
        patientExternalIds: Array.from(selectedPatientIds),
      }).unwrap();

      trackEvent(PatientEvents.reassignmentSubmission, {
        source: AnalyticsSourceValues.PATIENT_LIST,
      });

      handleClose();
      createToast({
        duration: 8000,
        icon: FAIconName.checkRegular,
        id: `${TOP_LEVEL_ID}__success-toast`,
        text: 'New provider matches may take 2-3 min. to appear in patient profile(s).',
        variant: 'success',
      });
    } catch (error) {
      logError(error, { source: 'AddReassignmentModal.handleSubmit' });
      handleClose();
      createToast({
        icon: FAIconName.exclamationTriangleRegular,
        id: `${TOP_LEVEL_ID}__error-toast`,
        text: "We're sorry, there was an error finding matches",
        variant: 'critical',
      });
    }
  };

  const providerDisplayName = selectedProvider?.primaryText ??
    (selectedProvider
      ? `${selectedProvider.displayFirstName ?? selectedProvider.firstName} ${selectedProvider.displayLastName ?? selectedProvider.lastName}`
      : '');

  const modalTitle =
    step === 1 ? 'New reassignment' : `Reassignment for ${providerDisplayName}`;

  const renderStep1 = () => (
    <>
      <ChakraFlex gap="12px" marginBottom="24px">
        {INITIATOR_TABS.map((tab) => {
          const isActive = initiatorTab === tab.value;
          return (
            <ChakraFlex
              key={tab.value}
              alignItems="center"
              aria-pressed={isActive}
              as="button"
              backgroundColor={isActive ? 'primary.10' : 'white'}
              border="1px solid"
              borderColor="neutral.line"
              borderRadius="16px"
              cursor="pointer"
              flex="1"
              gap="6px"
              height="32px"
              justifyContent="center"
              onClick={() => handleInitiatorTabChange(tab.value)}
              paddingX="18px"
            >
              {tab.value === 'EQUIP' ? (
                <img alt="" height={18} src={EquipLogo} width={18} />
              ) : (
                <Icon
                  color={isActive ? 'primary.100' : undefined}
                  name={FAIconName.userRegular}
                  size="sm"
                />
              )}
              <Text
                color={isActive ? 'primary.100' : undefined}
                variant="captionMedium"
              >
                {tab.label}
              </Text>
            </ChakraFlex>
          );
        })}
      </ChakraFlex>

      <ChakraFlex direction="column" gap="24px">
        <ChakraBox position="relative" zIndex={2}>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__provider-group`}
            isRequired
            label="Reassigning provider"
            shouldShowAsteriskWhenRequired
          >
            <ProviderUniversalSelect
              customPlaceholder="Search"
              handleSelect={handleProviderSelect}
              hideDropdownIndicator
              id={`${TOP_LEVEL_ID}__provider-select`}
              includedProviderTypes={INCLUDED_PROVIDER_TYPES}
              modalId={TOP_LEVEL_ID}
              selectedProvider={null}
              shouldClearValueOnSelect
              shouldIncludeOutOfOffice={false}
            />
            {selectedProvider && (
              <ChakraFlex
                alignItems="center"
                border="1px solid"
                borderColor="neutral.line"
                borderRadius="8px"
                justifyContent="space-between"
                marginTop="18px"
                padding="18px"
              >
                <ProviderAvatarBlock
                  avatarUrl={selectedProvider.avatar?.url}
                  id={`${TOP_LEVEL_ID}__selected-provider`}
                  legalName={{
                    firstName: selectedProvider.firstName,
                    lastName: selectedProvider.lastName,
                  }}
                  providerTypeName={getProviderTypeLabel(selectedProvider)}
                  shouldShowProviderType
                  variant="standard"
                />
                <FireflyIconButton
                  ariaLabel="Remove selected provider"
                  color="critical.base"
                  icon={FAIconName.timesRegular}
                  id={`${TOP_LEVEL_ID}__remove-provider`}
                  onClick={() => setSelectedProvider(null)}
                  size="small"
                  variant='tertiary'
                />
              </ChakraFlex>
            )}
          </InputGroupV2>
        </ChakraBox>

        {isEquipInitiated && (
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__ldoc-group`}
            isRequired
            label="Last day of ongoing patient care"
            shouldShowAsteriskWhenRequired
          >
            <DatePickerInput
              defaultDate={lastDayOfCare ? DateTime.fromISO(lastDayOfCare).toJSDate() : undefined}
              id={`${TOP_LEVEL_ID}__date-picker`}
              minDate={getTomorrow().toJSDate()}
              onSubmit={(date: Date) => {
                setLastDayOfCare(DateTime.fromJSDate(date).toISODate());
              }}
              placeholder="Select date"
            />
          </InputGroupV2>
        )}

        <InputGroupV2
          id={`${TOP_LEVEL_ID}__date-preference-group`}
          isRequired
          label="Reassignment date"
          shouldShowAsteriskWhenRequired
        >
          <DropdownV2
            id={`${TOP_LEVEL_ID}__date-preference`}
            menuOptions={datePreferenceOptions}
            onOptionClick={(value) =>
              setDatePreference((value as DatePreference) ?? null)
            }
            value={datePreference ?? undefined}
          />
        </InputGroupV2>

        <InputGroupV2
          id={`${TOP_LEVEL_ID}__reason-group`}
          isRequired
          label="Reassignment reason"
          shouldShowAsteriskWhenRequired
        >
          <DropdownV2
            id={`${TOP_LEVEL_ID}__reason`}
            menuOptions={filteredReasons}
            onOptionClick={(value) => setReasonCode(value ?? null)}
            value={reasonCode ?? undefined}
          />
        </InputGroupV2>
      </ChakraFlex>
    </>
  );

  const renderStep2 = () => (
    <>
      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        marginBottom="16px"
      >
        <ChakraFlex alignItems="center" gap="6px">
          <Text variant="bodyMedium">Select patients</Text>
          <Tooltip
            id={`${TOP_LEVEL_ID}__select-patients-tooltip`}
            placement="top"
            tooltipText="Patients will be reassigned to a new provider on set date"
          >
            <Icon
              aria-label="Select patients information"
              color="neutral.secondary"
              name={FAIconName.infoCircleRegular}
              size={IconSize.medium}
            />
          </Tooltip>
        </ChakraFlex>
        <ChakraCheckbox
          colorScheme="teal"
          id={`${TOP_LEVEL_ID}__select-all`}
          isChecked={allEligibleSelected}
          isDisabled={isSearching}
          onChange={handleSelectAll}
          sx={{
            '& .chakra-checkbox__control': {
              borderColor: isSearching ? undefined : 'primary.100',
            },
          }}
        >
          <Text
            color={isSearching ? 'neutral.tertiary' : 'primary.100'}
            variant="captionMedium"
          >
            Select all
          </Text>
        </ChakraCheckbox>
      </ChakraFlex>

      <ChakraInputGroup marginBottom="16px">
        <ChakraInputLeftElement pointerEvents="none">
          <Icon
            color="neutral.secondary"
            name={FAIconName.searchRegular}
            size={IconSize.medium}
          />
        </ChakraInputLeftElement>
        <ChakraInput
          borderRadius="12px"
          id={`${TOP_LEVEL_ID}__search`}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by patient name"
          value={searchQuery}
        />
        {isSearching && (
          <ChakraInputRightElement>
            <FireflyIconButton
              ariaLabel="Clear search"
              icon={FAIconName.timesRegular}
              id={`${TOP_LEVEL_ID}__clear-search`}
              onClick={() => setSearchQuery('')}
              size="small"
              variant="tertiary"
            />
          </ChakraInputRightElement>
        )}
      </ChakraInputGroup>

      <ChakraFlex direction="column" gap="18px" maxHeight="400px" overflowY="auto">
        {isFetchingPatients && (
          <SkeletonLoaderList
            direction="column"
            id={`${TOP_LEVEL_ID}__patient-skeleton`}
            itemCount={3}
            itemHeight="72px"
            itemWidth="100%"
          />
        )}
        {!isFetchingPatients && filteredPatients.length === 0 && (
          <Text variant="body">No patients found</Text>
        )}
        {!isFetchingPatients &&
          filteredPatients.map((patient) => {
            const eligible = isPatientEligible(patient);
            const isSelected = selectedPatientIds.has(
              patient.patientExternalId,
            );
            const hasPendingActive =
              patient.hasPendingReassignment && !patient.failureReason;

            let backgroundColor = 'white';
            if (isSelected) backgroundColor = 'primary.10';
            else if (hasPendingActive) backgroundColor = 'neutral.background.secondary';

            return (
              <ChakraFlex
                key={patient.patientExternalId}
                alignItems="center"
                aria-disabled={!eligible}
                aria-pressed={isSelected}
                as="button"
                backgroundColor={backgroundColor}
                border="1px solid"
                borderColor={isSelected ? 'primary.100' : 'neutral.line'}
                borderRadius="12px"
                cursor={eligible ? 'pointer' : 'default'}
                disabled={!eligible}
                gap="18px"
                onClick={() => handleTogglePatient(patient.patientExternalId)}
                opacity={eligible ? 1 : 0.5}
                padding="18px"
                width="100%"
              >
                <ChakraFlex direction="column" flex="1" textAlign="left">
                  <Text variant="bodyMedium">
                    {getPreferredFullName(patient.chosenName, patient.firstName, patient.lastName)}
                  </Text>
                  {patient.dateOfBirth && (
                    <Text color="neutral.secondary" variant="caption">
                      {DateTime.fromISO(patient.dateOfBirth).toFormat('MMM d, yyyy')}{' '}
                      ({getExactAgeFromDOB(patient.dateOfBirth, 'yyyy-MM-dd', 'long')})
                    </Text>
                  )}
                </ChakraFlex>
                {hasPendingActive && (
                  <Tag
                    color={TagColor.gray}
                    id={`${TOP_LEVEL_ID}__pending-tag-${patient.patientExternalId}`}
                    size="sm"
                    value="Pending reassignment"
                  />
                )}
                {isSelected && (
                  <Icon
                    color="primary.100"
                    name={FAIconName.checkRegular}
                    size={IconSize.medium}
                  />
                )}
              </ChakraFlex>
            );
          })}
      </ChakraFlex>
    </>
  );

  const renderFooter = () => (
    <ChakraFlex
      alignItems="center"
      justifyContent="space-between"
      width="100%"
    >
      {step === 1 ? (
        <>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel`}
            leftIcon={FAIconName.timesRegular}
            onClick={handleCancel}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__select-patients`}
            isDisabled={!isStep1Valid}
            onClick={() => setStep(2)}
            rightIcon={FAIconName.arrowRightRegular}
            variant="primary"
          >
            Select patients
          </FireflyButton>
        </>
      ) : (
        <>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__back`}
            leftIcon={FAIconName.arrowLeftRegular}
            onClick={() => setStep(1)}
            variant="secondary"
          >
            Back
          </FireflyButton>
          <ChakraFlex alignItems="center" gap="18px">
            {selectedPatientIds.size > 0 && (
              <Text color="neutral.secondary" variant="caption">
                {selectedPatientIds.size} selected
              </Text>
            )}
            <FireflyButton
              id={`${TOP_LEVEL_ID}__submit`}
              isDisabled={selectedPatientIds.size === 0}
              isLoading={isSubmitting}
              leftIcon={FAIconName.sparkleRegular}
              onClick={handleSubmit}
              variant="primary"
            >
              Find provider matches
            </FireflyButton>
          </ChakraFlex>
        </>
      )}
    </ChakraFlex>
  );

  return (
    <ModalV2
      bodyOverflow="visible"
      footerContent={renderFooter()}
      id={TOP_LEVEL_ID}
      isOpen={addReassignmentModal.isVisible}
      onClose={handleCancel}
      scrollBehavior="outside"
      size="lg"
      title={modalTitle}
    >
      <ChakraBox marginY="-18px" paddingY="24px">
        {step === 1 ? renderStep1() : renderStep2()}
      </ChakraBox>
    </ModalV2>
  );
};

export default AddReassignmentModal;
